import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { portalService } from '../../services/portalService';
import { fileService } from '../../services/fileService';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import FileUpload from '../../components/FileUpload';
import LoadingSpinner from '../../components/LoadingSpinner';

const { FiBuilding, FiPlus, FiEdit, FiTrash2, FiSave, FiX, FiLink, FiUpload } = FiIcons;

const AdminCompanies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoMethod, setLogoMethod] = useState('url'); // 'url' or 'upload'
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    logo: '',
    logoFile: null
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const data = await portalService.getUserCompanies();
      setCompanies(data);
    } catch (err) {
      console.error('Error fetching companies:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setUploading(true);
      let logoUrl = formData.logo;

      // If user uploaded a file, process it
      if (logoMethod === 'upload' && formData.logoFile) {
        try {
          // Validate and upload file
          fileService.validateImageFile(formData.logoFile);
          const uploadResult = await fileService.uploadFile(formData.logoFile);
          logoUrl = uploadResult.url;
        } catch (uploadError) {
          alert(`Upload error: ${uploadError.message}`);
          return;
        }
      }

      const companyData = {
        name: formData.name,
        description: formData.description,
        logo: logoUrl
      };

      if (editingId) {
        await portalService.updateCompany(editingId, companyData);
      } else {
        await portalService.createCompany(companyData);
      }

      await fetchCompanies();
      resetForm();
    } catch (err) {
      console.error('Error saving company:', err);
      alert('Error saving company. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (company) => {
    setEditingId(company.id);
    setFormData({
      name: company.name,
      description: company.description,
      logo: company.logo || '',
      logoFile: null
    });
    // Set logo method based on existing logo
    setLogoMethod(company.logo && company.logo.startsWith('http') ? 'url' : 'upload');
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this company?')) {
      try {
        await portalService.deleteCompany(id);
        await fetchCompanies();
      } catch (err) {
        console.error('Error deleting company:', err);
      }
    }
  };

  const handleFileSelect = (file, preview) => {
    setFormData(prev => ({
      ...prev,
      logoFile: file,
      logo: preview // Store preview for immediate display
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      logo: '',
      logoFile: null
    });
    setEditingId(null);
    setShowForm(false);
    setLogoMethod('url');
    setUploading(false);
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
              <SafeIcon icon={FiBuilding} className="h-8 w-8 text-primary-600" />
              <span>Manage Companies</span>
            </h1>
            <p className="mt-2 text-gray-600">
              Add, edit, and manage companies in the portal.
            </p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <SafeIcon icon={FiPlus} className="h-4 w-4" />
            <span>Add Company</span>
          </button>
        </div>
      </motion.div>

      {/* Form Modal */}
      {showForm && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4"
        >
          <motion.div
            initial={{ scale: 0.95 }}
            animate={{ scale: 1 }}
            className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingId ? 'Edit Company' : 'Add New Company'}
              </h3>
              <button
                onClick={resetForm}
                className="text-gray-400 hover:text-gray-600"
                disabled={uploading}
              >
                <SafeIcon icon={FiX} className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter company name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Brief description of the company"
                />
              </div>

              {/* Logo Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Company Logo
                </label>

                {/* Logo Method Selector */}
                <div className="mb-4">
                  <div className="flex space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="url"
                        checked={logoMethod === 'url'}
                        onChange={(e) => setLogoMethod(e.target.value)}
                        className="text-primary-600 focus:ring-primary-500"
                      />
                      <SafeIcon icon={FiLink} className="h-4 w-4 ml-2 mr-1" />
                      <span className="text-sm text-gray-700">URL</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="upload"
                        checked={logoMethod === 'upload'}
                        onChange={(e) => setLogoMethod(e.target.value)}
                        className="text-primary-600 focus:ring-primary-500"
                      />
                      <SafeIcon icon={FiUpload} className="h-4 w-4 ml-2 mr-1" />
                      <span className="text-sm text-gray-700">Upload File</span>
                    </label>
                  </div>
                </div>

                {logoMethod === 'url' ? (
                  <div>
                    <input
                      type="url"
                      value={formData.logo}
                      onChange={(e) => setFormData(prev => ({ ...prev, logo: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="https://example.com/logo.png"
                    />
                    {formData.logo && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-600 mb-2">Preview:</p>
                        <div className="h-16 w-16 bg-white rounded border flex items-center justify-center p-1">
                          <img
                            src={formData.logo}
                            alt="Logo preview"
                            className="max-w-full max-h-full object-contain"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'block';
                            }}
                          />
                          <div className="text-sm text-red-500 mt-2" style={{ display: 'none' }}>
                            Failed to load image from URL
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <FileUpload
                    onFileSelect={handleFileSelect}
                    currentImage={formData.logo}
                    accept="image/png,image/jpeg,image/jpg"
                  />
                )}
              </div>

              <div className="flex space-x-3 pt-4 border-t">
                <button
                  type="submit"
                  disabled={uploading}
                  className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? (
                    <>
                      <LoadingSpinner size="sm" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiSave} className="h-4 w-4" />
                      <span>{editingId ? 'Update' : 'Create'}</span>
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  disabled={uploading}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}

      {/* Companies Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Assets
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                [...Array(3)].map((_, i) => (
                  <tr key={i}>
                    <td className="px-6 py-4">
                      <div className="animate-pulse flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
                        <div className="h-4 bg-gray-200 rounded w-32"></div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="animate-pulse h-4 bg-gray-200 rounded w-48"></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="animate-pulse h-4 bg-gray-200 rounded w-16"></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="animate-pulse flex justify-end space-x-2">
                        <div className="w-8 h-8 bg-gray-200 rounded"></div>
                        <div className="w-8 h-8 bg-gray-200 rounded"></div>
                      </div>
                    </td>
                  </tr>
                ))
              ) : companies.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center text-gray-500">
                    No companies found. Add your first company to get started.
                  </td>
                </tr>
              ) : (
                companies.map((company) => (
                  <tr key={company.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-3">
                        {company.logo ? (
                          <div className="h-10 w-10 rounded-lg border border-gray-200 bg-white flex items-center justify-center p-1">
                            <img
                              src={company.logo}
                              alt={`${company.name} logo`}
                              className="max-w-full max-h-full object-contain"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            <div className="h-10 w-10 rounded-lg bg-gray-200 flex items-center justify-center" style={{ display: 'none' }}>
                              <SafeIcon icon={FiBuilding} className="h-5 w-5 text-gray-500" />
                            </div>
                          </div>
                        ) : (
                          <div className="h-10 w-10 rounded-lg bg-gray-200 flex items-center justify-center">
                            <SafeIcon icon={FiBuilding} className="h-5 w-5 text-gray-500" />
                          </div>
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {company.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {company.id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs truncate">
                        {company.description}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {company.assetsCount}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleEdit(company)}
                          className="text-primary-600 hover:text-primary-700 p-1"
                          title="Edit"
                        >
                          <SafeIcon icon={FiEdit} className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(company.id)}
                          className="text-red-600 hover:text-red-700 p-1"
                          title="Delete"
                        >
                          <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminCompanies;